import { featureFlags } from '@seen/core/featureFlags'
import { getReferenceString, referenceStringToId } from '@seen/core/references'
import { InsertConditionsButtonNode as InsertConditionsButtonSchema } from '@seen/core/tiptap/schema'
import { Button } from '@seen/ui'
import { IconClipboardPlus, IconLoader2 } from '@tabler/icons-react'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { type JSX, memo, useCallback, useState } from 'react'
import { toast } from 'sonner'
import {
  noteButtonClass,
  noteButtonLabelClass,
} from '#src/components/rich-text-editor/noteButtonClass.ts'
import { useOptionalParticipantReference } from '#src/contexts/index.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { fetchOrderedCurrentConditions } from '#src/hooks/useOrderedConditions.ts'

export const InsertConditionsButtonNode = InsertConditionsButtonSchema.extend({
  addNodeView() {
    return ReactNodeViewRenderer(InsertConditionsButtonNodeComponent)
  },
})

function InsertConditionsButtonNodeComponent(
  props: NodeViewProps
): JSX.Element {
  return (
    <NodeViewWrapper className="inline-flex">
      <InsertConditionsButtonNodeComponentInner {...props} />
    </NodeViewWrapper>
  )
}

const InsertConditionsButtonNodeComponentInner = memo(
  function InsertConditionsButtonNodeComponentInner({
    editor,
    deleteNode,
    getPos,
  }: NodeViewProps): JSX.Element {
    const medplum = useMedplum()
    const facesheetImprovements = useFeatureFlag(
      featureFlags.facesheetImprovements
    )
    const participantReference = useOptionalParticipantReference()
    const getPosRef = useCurrentRef(getPos)
    const [isLoading, setIsLoading] = useState(false)

    const handleClick = useCallback(async () => {
      if (!participantReference) {
        toast.error('No participant context available')
        return
      }

      setIsLoading(true)

      try {
        const sortedConditions = await fetchOrderedCurrentConditions(
          medplum,
          participantReference,
          { manualOrder: !facesheetImprovements }
        )

        const pos = getPosRef.current()
        if (pos === undefined) {
          return
        }
        deleteNode()

        if (sortedConditions.length === 0) {
          editor
            .chain()
            .insertContentAt(pos, {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'italic' }],
                  text: 'No current conditions',
                },
              ],
            })
            .focus()
            .run()
          return
        }

        const patientId = referenceStringToId(
          getReferenceString(participantReference)
        )

        const bulletListContent = {
          type: 'bulletList',
          content: sortedConditions.map((condition) => ({
            type: 'listItem',
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    marks: [
                      {
                        type: 'link',
                        attrs: {
                          href: `/Patient/${patientId}/conditions/${condition.id}`,
                        },
                      },
                    ],
                    text: condition.name,
                  },
                ],
              },
            ],
          })),
        }

        editor.chain().insertContentAt(pos, bulletListContent).focus().run()
      } catch (_error) {
        toast.error('Failed to fetch conditions')
        setIsLoading(false)
      }
    }, [
      medplum,
      facesheetImprovements,
      participantReference,
      getPosRef,
      editor,
      deleteNode,
    ])

    const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
    return (
      <Button
        variant="unstyled"
        size="unstyled"
        className={noteButtonClass(inlineCapture)}
        disabled={!editor.isEditable || isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <IconLoader2 className="size-3.5 shrink-0 animate-spin text-foreground-secondary" />
        ) : (
          <IconClipboardPlus className="size-3.5 shrink-0 text-foreground-secondary" />
        )}
        <span className={noteButtonLabelClass(inlineCapture)}>
          Insert Past Medical History
        </span>
      </Button>
    )
  }
)
